import React, { useState, useEffect, useRef } from "react";
import { Dialog, IconButton, Button, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import AdaChatAIIcon from "../../images/adachatai.png";
import { useEnumStrings } from "../enumStrings";
import { useIdaGenerateMutation, IdaEntityType, IdaGenerateMode } from "../../graphql/types";
import { showActionErrorNotice } from "./ActionErrorNotice";

const useStyles = makeStyles((theme: any) => ({
  dialogPaper: {
    borderRadius: "20px",
    maxWidth: "520px",
    width: "520px",
    overflow: "hidden",
    padding: "0",
    backgroundColor: "#FFFFFF",
    position: "relative" as const,
    "@media (max-width: 600px)": {
      borderRadius: "16px",
      maxWidth: "calc(100% - 24px)",
      width: "calc(100% - 24px)",
      margin: "12px",
    },
  },
  closeButton: {
    position: "absolute" as const,
    right: "10px",
    top: "10px",
    backgroundColor: "#ED1C24",
    color: "white",
    width: "28px",
    height: "28px",
    padding: 0,
    zIndex: 1,
    "&:hover": { backgroundColor: "#D41920" },
  },
  mascotRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "4px",
    marginBottom: "8px",
    paddingLeft: "20px",
    paddingTop: "16px",
  },
  mascotImg: {
    width: "80px",
    height: "80px",
    objectFit: "contain" as const,
  },
  speechBubble: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #E1A731",
    borderRadius: "12px 12px 12px 0",
    padding: "6px 12px",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "12px",
    fontWeight: 600,
    color: "#24272C",
    whiteSpace: "nowrap" as const,
    marginBottom: "8px",
  },
  title: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "18px",
    color: theme.palette.primary.main,
    textAlign: "left" as const,
    lineHeight: "1.4",
    margin: "0 24px 8px 24px",
  },
  subtitle: {
    fontFamily: "DM Sans, sans-serif",
    fontSize: "13px",
    color: "#333",
    textAlign: "left" as const,
    lineHeight: "1.5",
    margin: "0 24px 12px 24px",
  },
  textarea: {
    margin: "0 24px",
    border: `1.5px solid ${theme.palette.primary.main}`,
    borderRadius: "8px",
    padding: "12px",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "13px",
    lineHeight: "1.6",
    resize: "vertical" as const,
    minHeight: "100px",
    outline: "none",
    "&:focus": { borderColor: theme.palette.primary.main },
  },
  generateBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "14px",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "uppercase" as const,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "16px auto 20px auto",
    "&:hover": { backgroundColor: "#D4A353" },
  },
}));

interface IDAGenerateModalProps {
  open: boolean;
  onClose: () => void;
  keyDetails: string;
  onKeyDetailsChange: (value: string) => void;
  onGenerate: (generatedText: string) => void;
  entityName: string;
  entityType?: "company" | "chamber";
  mode?: "companyDescription" | "perkTitle" | "perkDescription";
  customTitle?: string;
  customSubtitle?: string;
  customSubtitleLine2?: string;
  customPlaceholder?: string;
  maxLength?: number;
}

const modeMap: Record<string, IdaGenerateMode> = {
  companyDescription: IdaGenerateMode.CompanyDescription,
  perkTitle: IdaGenerateMode.PerkTitle,
  perkDescription: IdaGenerateMode.PerkDescription,
};

const entityTypeMap: Record<string, IdaEntityType> = {
  company: IdaEntityType.Company,
  chamber: IdaEntityType.Chamber,
};

const IDAGenerateModal: React.FC<IDAGenerateModalProps> = ({
  open,
  onClose,
  keyDetails,
  onKeyDetailsChange,
  onGenerate,
  entityName,
  entityType = "company",
  mode = "companyDescription",
  customTitle,
  customSubtitle,
  customSubtitleLine2,
  customPlaceholder,
  maxLength,
}) => {
  const classes = useStyles();
  const enumStrings = useEnumStrings() as any;
  const [generating, setGenerating] = useState(false);
  const [idaGenerateMutation] = useIdaGenerateMutation();
  const hasAutoGeneratedRef = useRef(false);

  const clampText = (text: string, limit?: number) => {
    if (!limit || limit <= 0) return text.trim();
    return text.trim().slice(0, limit);
  };

  const handleGenerate = async () => {
    setGenerating(true);

    try {
      const { data } = await idaGenerateMutation({
        variables: {
          input: {
            entityName,
            entityType: entityTypeMap[entityType] || IdaEntityType.Company,
            mode: modeMap[mode] || IdaGenerateMode.CompanyDescription,
            keyDetails: keyDetails.trim(),
            ...(maxLength ? { maxLength } : {}),
          },
        },
      });

      if (data?.idaGenerate?.success && data.idaGenerate.generatedText) {
        onGenerate(clampText(data.idaGenerate.generatedText, maxLength));
      } else {
        console.error("IDA generation failed:", data);
      }
    } catch (error) {
      console.error("Error calling idaGenerate mutation:", error);
      showActionErrorNotice(error);
    }

    setGenerating(false);
  };

  useEffect(() => {
    if (open && !hasAutoGeneratedRef.current) {
      hasAutoGeneratedRef.current = true;
      handleGenerate();
    }
    if (!open) {
      hasAutoGeneratedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
      <IconButton className={classes.closeButton} onClick={onClose}>
        <CloseIcon style={{ fontSize: "18px" }} />
      </IconButton>
      <div className={classes.mascotRow}>
        <img src={AdaChatAIIcon} alt="IDA" className={classes.mascotImg} />
      </div>
      <div className={classes.title}>
        {customTitle || enumStrings.idaGenerate.title}
      </div>
      <div className={classes.subtitle}>
        {enumStrings.idaGenerate.generating}
      </div>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "120px" }}>
        <CircularProgress size={36} style={{ color: "#E1A731" }} />
      </div>
    </Dialog>
  );
};

export default IDAGenerateModal;
